import type { PerformanceResult } from "@/schemas";
import { cn } from "@/lib/utils";

export interface CashPosition {
  asset_id: string;
  symbol: string;
  name: string;
  shares_owned: number;  // Balance in the currency
  current_value: number; // Value in base currency
  performance_absolute: number;  // FX P&L (0 for base currency)
  is_base_currency?: boolean;
}

interface CashTableProps {
  cashPositions: CashPosition[];
  baseCurrencyCode: string;
  className?: string;
  headerLabel?: string;
  showTopBorder?: boolean;
}

export function getCashPositions(
  performanceData: PerformanceResult | null,
  baseCurrencyCode: string,
): CashPosition[] {
  if (!performanceData?.data?.assets_performance) {
    return [];
  }

  return Object.entries(performanceData.data.assets_performance)
    .filter(([, asset]) => asset.asset_type === "fiat")
    .map(([assetId, asset]) => ({
      asset_id: assetId,
      symbol: asset.asset_symbol,
      name: asset.asset_name || asset.asset_symbol,
      shares_owned: asset.shares_owned ?? 0,
      current_value: asset.current_value ?? 0,
      performance_absolute: asset.performance_absolute ?? 0,
      is_base_currency:
        assetId === baseCurrencyCode.toUpperCase() ||
        asset.asset_symbol === baseCurrencyCode,
    }));
}

export function CashTable({
  cashPositions,
  baseCurrencyCode,
  className,
  headerLabel = "Cash & Liquidity",
  showTopBorder = true,
}: CashTableProps) {
  const formatCurrency = (value: number, currencyCode: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatBalance = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  };

  if (cashPositions.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "w-full overflow-x-auto",
        showTopBorder && "border-t border-border/40",
        className,
      )}
    >
      <div className="min-w-[500px]">
        <div className="px-6 py-3 bg-muted/20 flex items-center gap-2 border-b border-border/20">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {headerLabel}
          </span>
        </div>
        <div className="divide-y divide-border/20">
        {cashPositions.map((position) => {
          const isBaseCurrency = position.asset_id === baseCurrencyCode || position.is_base_currency;
          const hasFxPnl = !isBaseCurrency && Math.abs(position.performance_absolute) > 0.01;
          const isPositiveFxPnl = position.performance_absolute >= 0;
          
          return (
            <div 
              key={position.asset_id}
              className="px-6 py-3 flex items-center justify-between hover:bg-muted/10 transition-colors"
            >
              {/* Currency info */}
              <div className="flex items-center gap-3 min-w-[140px]">
                <div className="h-8 w-8 rounded-full bg-muted/50 flex items-center justify-center text-xs font-bold">
                  {position.symbol.slice(0, 2)}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">{position.name}</span>
                  <span className="text-[10px] text-muted-foreground uppercase">{position.symbol}</span>
                </div>
              </div>
              
              {/* Balance */}
              <div className="text-right min-w-[100px]">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">Balance</div>
                <div className="font-mono text-sm font-medium">
                  {formatBalance(position.shares_owned)}
                </div>
              </div>
              
              {/* Value in base currency */}
              <div className="text-right min-w-[120px]">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
                  Value ({baseCurrencyCode})
                </div>
                <div className="font-mono text-sm font-semibold">
                  {formatCurrency(position.current_value, baseCurrencyCode)}
                </div>
              </div>
              
              {/* FX P&L (only for foreign currencies) */}
              <div className="text-right min-w-[100px]">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">FX P&L</div>
                {hasFxPnl ? (
                  <div 
                    className={cn(
                      "font-mono text-sm font-medium",
                      isPositiveFxPnl ? "text-emerald-500" : "text-red-500"
                    )}
                  >
                    {isPositiveFxPnl ? "+" : ""}{formatCurrency(position.performance_absolute, baseCurrencyCode)}
                  </div>
                ) : (
                  <div className="text-muted-foreground text-sm">—</div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
